import React, { useContext, useState, useMemo } from "react";
import { useTracking } from "@local-logic/core/tracking";
import { mapUtils } from "@local-logic/core/utils";
import { POI, School } from "@local-logic/client";

import { useGeoLocationInfo } from "~/hooks/useGeoLocationInfo";
import useEffectOnce from "~/hooks/useEffectOnce";
import { useStateManagerContext } from "~/context/stateManager";
import { type LocationInfo } from "~/types/profiles";
import { type Geography } from "@local-logic/client";
import errorTracker from "~/utils/errorTracker";

import { useMarkers } from "./hooks/useMarkers";
import {
  type NeighborhoodMapProps,
  type POICategory,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "./types";
import { radiusOptions, categories } from "./constants";

interface Location {
  label?: string;
  lat: number;
  lng: number;
}

export type DefaultContext = {
  isLoading: boolean;
  radius: number;
  locationInfo?: LocationInfo;
  neighborhoodGeometry?: ReturnType<typeof mapUtils.formatGeometry>;
  activePoiCategory?: POICategory;
  onSelectPoiCategory: (category?: POICategory) => void;
  onRadiusChange: (newRadius: number) => void;
  markers?: POI[] | School[];
};

const NeighborhoodMapContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);
const useNeighborhoodMap = () => useContext(NeighborhoodMapContext);

const NeighborhoodMapProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { sdkState } = useStateManagerContext<NeighborhoodMapProps>();
  const locale = sdkState.globalOptions?.locale || "en";
  const tracking = useTracking();
  const [activePoiCategory, setActivePoiCategory] = useState<
    DefaultContext["activePoiCategory"]
  >(Object.keys(categories)[0] as DefaultContext["activePoiCategory"]);
  const [radius, setRadius] = useState(
    radiusOptions[sdkState.options?.distanceUnit || "metric"][0]
  );

  const radiusToMeters = useMemo(() => {
    // Default, km to m = km * 1000
    let multiplier = 1000;

    if (sdkState.options?.distanceUnit === "imperial") {
      // Imperial = mi to m = mi x the multiplier (converting MILES to METERS)
      multiplier = 1609.344;
    }

    return radius * multiplier;
  }, [radius]);

  useEffectOnce(() => {
    tracking.postEvent({
      eventType: TrackingPipelineEvents.SDK_VIEWED,
      eventAttributes: [
        {
          label: "default_category",
          data: activePoiCategory,
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SDK_VIEWED,
      eventAttributes: {
        ll_default_category: activePoiCategory,
      },
    });
  });

  const {
    isLoading: isGeoLocationInfoLoading,
    locationInfo,
    geographies,
  } = useGeoLocationInfo({
    lat: sdkState.options?.lat,
    lng: sdkState.options?.lng,
    geographyId: sdkState.options?.geographyId,
    locale,
    include: "geometry",
  });

  const { data: markers, isLoading: isMarkersLoading } = useMarkers({
    lat: locationInfo?.coordinates.lat,
    lng: locationInfo?.coordinates.lng,
    category: activePoiCategory,
    radius: radiusToMeters,
  });

  const neighborhoodGeometry = useMemo(() => {
    if (isGeoLocationInfoLoading) return undefined;

    let selectedGeography: Geography | undefined;

    if (sdkState.options?.geographyId) {
      selectedGeography = Object.values(geographies || {}).find(
        (geography) =>
          geography.level ===
          Number(sdkState.options?.geographyId?.substring(1, 3))
      );
    } else {
      selectedGeography =
        Object.values(geographies || {}).sort(
          (a: Geography, b: Geography) => a.level - b.level
        )[0] || undefined;
    }

    if (!selectedGeography) return undefined;

    return mapUtils.formatGeometry(selectedGeography.geometry as string[][]);
  }, [geographies, isGeoLocationInfoLoading]);

  const handleRadiusChange = (newRadius: number) => {
    tracking.postEvent({
      eventType: TrackingPipelineEvents.SEARCH_RADIUS_CHANGED,
      eventAttributes: [
        {
          label: "previous_radius",
          data: radius,
        },
        {
          label: "new_radius",
          data: newRadius,
        },
        {
          label: "distance_unit",
          data: sdkState.options?.distanceUnit || "metric",
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SEARCH_RADIUS_CHANGED,
      eventAttributes: {
        ll_previous_radius: radius,
        ll_new_radius: newRadius,
        ll_distance_unit: sdkState.options?.distanceUnit || "metric",
      },
    });

    errorTracker.addBreadCrumb(
      "NeighborhoodMapProvider",
      "handleRadiusChange",
      {
        newRadius,
      }
    );

    setRadius(newRadius);
  };

  const handleSelectPoiCategory = (category?: POICategory) => {
    errorTracker.addBreadCrumb(
      "NeighborhoodMapProvider",
      "handleSelectPoiCategory",
      {
        category,
      }
    );

    setActivePoiCategory(activePoiCategory === category ? undefined : category);
  };

  const isLoading = useMemo(
    () => isGeoLocationInfoLoading || isMarkersLoading,
    [isGeoLocationInfoLoading, isMarkersLoading]
  );

  return (
    <NeighborhoodMapContext.Provider
      value={{
        isLoading,
        radius,
        onRadiusChange: handleRadiusChange,
        locationInfo,
        neighborhoodGeometry,
        activePoiCategory,
        onSelectPoiCategory: handleSelectPoiCategory,
        markers,
      }}
    >
      {children}
    </NeighborhoodMapContext.Provider>
  );
};

export { useNeighborhoodMap, NeighborhoodMapProvider, type Location };
